import { BaseTextFieldProps, TextField } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { memo, useCallback, useMemo } from 'react';
import { resolveSxProps } from 'utility/styles';
import { LabelWithTooltip } from '../components/LabelWithTooltip';
import { DynamicFieldRendererProps } from '../DynamicFormField';
import { getFieldLabel } from '../utility/functions';
import { isFiniteNumber } from 'utility/functions';

/**
 * ---------------------------------------
 * Text Dynamic Field
 * - Numeric, Text, Multiline
 * ---------------------------------------
 *
 */

type TextDynamicFieldProps = {
  fieldConfig: DynamicFieldRendererProps['fieldConfig'];
  tooltipText: DynamicFieldRendererProps['tooltipText'];
  fieldText: DynamicFieldRendererProps['fieldText'];
  setFieldText: DynamicFieldRendererProps['setFieldText'];
  setIsUserEditing: DynamicFieldRendererProps['setIsUserEditing'];
  debouncedUpdate: DynamicFieldRendererProps['debouncedUpdate'];
  disabled: DynamicFieldRendererProps['disabled'];
  isDynamicOperator: DynamicFieldRendererProps['isDynamicOperator'];
  fieldSx?: DynamicFieldRendererProps['fieldSx'];
};

export const TextDynamicField = memo(
  ({
    fieldConfig,
    tooltipText,
    fieldText,
    setFieldText,
    setIsUserEditing,
    debouncedUpdate,
    disabled,
    isDynamicOperator,
    fieldSx,
  }: TextDynamicFieldProps) => {
    const theme = useTheme();

    // Styles and related props
    const resolvedSx: SxProps<Theme> = useMemo(() => {
      return resolveSxProps(fieldSx, theme);
    }, [fieldSx, theme]);

    // ---------------------------
    // Setup field config props
    // ---------------------------

    // Clean string label for mui outlined
    const fieldLabel = useMemo(() => {
      return getFieldLabel(fieldConfig);
    }, [fieldConfig]);

    const fieldName = useMemo(() => {
      return fieldConfig?.name ?? '';
    }, [fieldConfig?.name]);

    // Discern number vs textual type
    const fieldType = useMemo(() => {
      return fieldConfig.type === 'number' ? 'number' : 'text';
    }, [fieldConfig.type]);

    const isTextArea = useMemo(() => {
      return fieldConfig?.type === 'textarea';
    }, [fieldConfig?.type]);

    const testId = useMemo(() => {
      return `dynamic-field-${fieldName}-${fieldType}`;
    }, [fieldName, fieldType]);

    const isDisabled = useMemo(() => {
      return isDynamicOperator || disabled;
    }, [isDynamicOperator, disabled]);

    const isNumberType = useMemo(() => {
      return fieldConfig?.type === 'number';
    }, [fieldConfig?.type]);

    const isIntegerFmt = useMemo(() => {
      return fieldConfig?.config?.format === 'integer';
    }, [fieldConfig?.config?.format]);

    const minConfig = useMemo(() => {
      return fieldConfig?.config?.min;
    }, [fieldConfig?.config?.min]);

    const maxConfig = useMemo(() => {
      return fieldConfig?.config?.max;
    }, [fieldConfig?.config?.max]);

    const stepConfig = useMemo(() => {
      return fieldConfig?.config?.step;
    }, [fieldConfig?.config?.step]);

    // Base HTML input props
    const inputProps: BaseTextFieldProps['inputProps'] = useMemo(() => {
      const props: BaseTextFieldProps['inputProps'] = {
        'data-testid': `${testId}-input`,
        min: minConfig,
        max: maxConfig,
        step: stepConfig,
      };
      return props;
    }, [testId, maxConfig, minConfig, stepConfig]);

    // Multiline area props
    const multilineProps = useMemo(() => {
      if (isTextArea) {
        return {
          multiline: true,
          minRows: 3,
          maxRows: 6,
        };
      } else {
        return {
          multiline: false,
          minRows: undefined,
          maxRows: undefined,
        };
      }
    }, [isTextArea]);

    /**
     * Numeric inputs handling
     * Set of blocked keys for number inputs based on field config.
     *
     * There is additional handling in onChange / onKeyDown
     * like checking e.target.validity.badInput to catch input
     * which the browser is unable to convert to a valid number
     * but is a 'number' by technical js standards (eg: "55--2", "1e2", etc.)
     * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState/badInput
     *
     * for advanced usage the parent onChange handler may need
     * to also cleanup or prop drill custom callbacks down for
     * onKeyDown/onChanges to handle more specific edge cases.
     */
    const numBlockKeys = useMemo(() => {
      // Blocked keys for numeric inputs
      const baseKeys: string[] = ['+', 'e', 'E'];
      // Block decimals for integer format
      if (isIntegerFmt) {
        baseKeys.push('.', ',');
      }
      // Check if negative is allowed based on min config
      // note: sometimes the api returns this as a string
      const nMinConfig = minConfig ?? null;
      const minValue =
        typeof nMinConfig === 'string'
          ? parseFloat(nMinConfig)
          : typeof nMinConfig === 'number'
            ? nMinConfig
            : null;
      const minIsNumber = isFiniteNumber(minValue);
      // If min was a valid number and it is greater
      // than or equal to 0 we can block the negative
      if (minIsNumber && minValue !== null && minValue >= 0) {
        baseKeys.push('-');
      }

      return baseKeys;
    }, [isIntegerFmt, minConfig]);

    // -------------------------
    // Event handlers
    // -------------------------
    const handleOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // Prevent update on invalid number input
        // eg: "22--1", "1e2", "+42", etc...
        if (isNumberType && e.target.validity.badInput) {
          return;
        }
        const val = e.target.value;
        setFieldText(val);
        setIsUserEditing(true);
        debouncedUpdate(val);
      },
      [setFieldText, setIsUserEditing, isNumberType, debouncedUpdate],
    );

    // Handle blocked keys for numeric types
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isNumberType) {
          if (numBlockKeys.includes(e.key)) {
            e.preventDefault();
            return;
          }
        }
      },
      [isNumberType, numBlockKeys],
    );

    /**
     * handling onPaste also is helpful but
     * may require having clipboard access
     * in some browser edge cases...
     */
    const handleOnPaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (isNumberType) {
          try {
            const pasteData = e.clipboardData.getData('text');
            const hasBlockedKeys = numBlockKeys.some((key) => pasteData.includes(key));
            if (hasBlockedKeys) {
              e.preventDefault();
              return;
            }
          } catch (err) {
            console.error('[TextDynamicField handleOnPaste]:', err);
          }
        }
      },
      [isNumberType, numBlockKeys],
    );

    return (
      <TextField
        data-testid={testId}
        type={fieldType}
        label={
          <LabelWithTooltip
            label={fieldLabel}
            tooltip={tooltipText}
          />
        }
        value={fieldText}
        fullWidth={true}
        variant="outlined"
        multiline={multilineProps.multiline}
        minRows={multilineProps.minRows}
        maxRows={multilineProps.maxRows}
        disabled={isDisabled}
        onChange={handleOnChange}
        onKeyDown={handleKeyDown}
        onPaste={handleOnPaste}
        sx={resolvedSx}
        slotProps={{
          htmlInput: inputProps,
          inputLabel: { shrink: true, sx: { zIndex: 0 } },
        }}
      />
    );
  },
);
TextDynamicField.displayName = 'TextDynamicField';
